import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { ArrowLeft, Ban, Mail, ReceiptText, ShieldCheck, Zap } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Tabs } from '@/components/ui/Tabs'
import { useToast } from '@/components/ui/Toast'

import {
  PlayerBanHistoryList,
  PlayerBattlesList,
  PlayerCardsGrid,
  PlayerGoldHistoryList,
  PlayerMailsList,
  PlayerSidebarCards,
  PlayerTopCards,
  PlayerTransactionsList,
} from '@/components/players'
import { ConfirmDialog, type ConfirmRequest } from '@/components/shared'
import {
  banHistoryForPlayer,
  battlesForPlayer,
  cardsForPlayer,
  goldHistoryForPlayer,
  mailsForPlayer,
  PLAYERS,
  transactionsForPlayer,
} from '@/fixtures'
import type { Player } from '@/models'

type TabKey = 'cardCollection' | 'mail' | 'transaction' | 'banInfo' | 'matchHistory' | 'caraHistory'

const TAB_OPTIONS: { value: TabKey; label: string }[] = [
  { value: 'cardCollection', label: 'Cards' },
  { value: 'mail', label: 'Mail' },
  { value: 'transaction', label: 'Transactions' },
  { value: 'banInfo', label: 'Ban Info' },
  { value: 'matchHistory', label: 'Matches' },
  { value: 'caraHistory', label: 'Cara' },
]

/* The id arrives as a prop rather than from a route match: in Inertia the
 * controller resolves the record and hands it down, so this is the first place
 * a real props contract starts to take shape. */
export function PlayerDetailPage({ playerId }: { playerId: string }) {
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState<TabKey>('cardCollection')
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null)
  const [playersList, setPlayersList] = useState<Player[]>(PLAYERS)

  const cleanId = (id?: string) => id?.replace(/^#/, '').toLowerCase() ?? ''
  const player = playersList.find((row) => cleanId(row.id) === cleanId(playerId))

  if (!player) {
    return (
      <EmptyState
        title="Player not found"
        description={`No player with id ${playerId}.`}
        action={
          <Button variant="outline" asChild>
            <Link href="/players">Back to players</Link>
          </Button>
        }
      />
    )
  }

  const toggleBan = () => {
    if (player.status === 'Banned') {
      setPlayersList((prev) =>
        prev.map((p) => (p.id === player.id ? { ...p, status: 'Active' } : p)),
      )
      toast(`${player.username} unbanned`, 'success')
    } else {
      setConfirm({
        title: `Ban ${player.username}?`,
        description:
          'They lose access immediately and their trophies are frozen. This action will be logged in audit trail.',
        confirmLabel: 'Ban Player',
        destructive: true,
        onConfirm: () => {
          setPlayersList((prev) =>
            prev.map((p) => (p.id === player.id ? { ...p, status: 'Banned' } : p)),
          )
          toast(`${player.username} banned`, 'danger')
        },
      })
    }
  }

  const battles = battlesForPlayer(player)
  const transactions = transactionsForPlayer(player)
  const goldHistory = goldHistoryForPlayer(player)
  const mails = mailsForPlayer(player)
  const cards = cardsForPlayer(player)
  const banHistory = banHistoryForPlayer(player)

  return (
    <div className="space-y-6">
      {/* Top Back Nav & Status */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/players">
            <ArrowLeft className="size-4" />
            Back to players
          </Link>
        </Button>
        <Badge variant={player.status === 'Active' ? 'success' : 'danger'}>{player.status}</Badge>
      </div>

      {/* One 7 / 3 grid for the whole page.
          Quick Actions used to share the top row with the profile, rank and
          SVIP cards, so any extra button in it stretched that row and shoved
          the tabs down the page. As a column of its own it can grow as tall as
          it likes and nothing on the left moves. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-10">
        <div className="space-y-4 lg:col-span-7">
          <PlayerTopCards player={player} />

          <Tabs
            aria-label="Player profile sections"
            options={TAB_OPTIONS}
            value={activeTab}
            onValueChange={(next) => setActiveTab(next as TabKey)}
          />

          {/* Active Tab Panel */}
          {activeTab === 'mail' && <PlayerMailsList mails={mails} />}
          {activeTab === 'transaction' && <PlayerTransactionsList transactions={transactions} />}
          {activeTab === 'banInfo' && <PlayerBanHistoryList records={banHistory} />}
          {activeTab === 'matchHistory' && <PlayerBattlesList battles={battles} />}
          {activeTab === 'caraHistory' && <PlayerGoldHistoryList entries={goldHistory} />}
          {activeTab === 'cardCollection' && <PlayerCardsGrid cards={cards} />}
        </div>

        <div className="space-y-4 lg:col-span-3">
          <Card className="px-5 py-4">
            <h3 className="text-fg m-0 mb-3.5 flex items-center gap-2 text-sm font-semibold">
              <Zap aria-hidden className="text-fg-subtle size-4" />
              Quick Actions
            </h3>
            {/* Roomier than a list of rows normally needs: three buttons in a
                narrow column read as a wall unless they are given air, and the
                destructive one wants space around it rather than a neighbour
                a mis-click away. */}
            <div className="flex flex-col gap-2.5">
              <Button variant="outline" className="w-full justify-start gap-2">
                <ReceiptText aria-hidden className="text-fg-subtle" />
                Manual Transaction
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2">
                <Mail aria-hidden className="text-fg-subtle" />
                Send Mail
              </Button>
              <Button
                variant={player.status === 'Banned' ? 'outline' : 'destructive'}
                onClick={toggleBan}
                className="mt-1 w-full justify-start gap-2"
              >
                {player.status === 'Banned' ? (
                  <>
                    <ShieldCheck aria-hidden />
                    Unban
                  </>
                ) : (
                  <>
                    <Ban aria-hidden />
                    Ban Player
                  </>
                )}
              </Button>
            </div>
          </Card>

          <PlayerSidebarCards player={player} />
        </div>
      </div>

      <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
    </div>
  )
}
